// Inspecciones (F3a): lógica pura, sin base de datos.
//  - Estado de un hallazgo con "vencido" calculado al vuelo.
//  - Próxima fecha de una programación por su periodicidad (sin corrimiento de fin de mes).
//  - "No cumple" de cada ítem, foto obligatoria, ítems sin responder y hallazgos automáticos.
//  - Filas de la vista tipo tabla (estado, plantilla, sede, responsable, fecha, hallazgos abiertos y vencidos), filtros,
//    agrupación por estado o por sede, y la cuadrícula del calendario.
//  - Validación de plantillas propias y de programaciones; texto canónico que se firma (hash, K6).

import { fechaEc as fechaEcComun } from '../fechas';
import { EXTINTOR_DISTANCIA_NACIONAL, EXTINTOR_DISTANCIA_QUITO } from '../pe/pe-constantes-normativa';
import type {
  ActivoMin, EstadoHallazgo, EstadoInspeccion, FilaArchivo, FilaHallazgo, FilaInspeccion, FilaProgramacion, FilaRespuesta, FirmaInspeccion,
  FotoPlantilla, Frecuencia, ItemFoto, Riesgo, SedeMin, TipoRespuesta, TrabajadorMin,
} from './insp-tipos';
import { ETIQUETA_ROL_FIRMA } from './insp-tipos';

// ── Fechas ───────────────────────────────────────────────────────────────────

export const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** dd/mm/aaaa: una fecha sin hora, tal cual; un instante (signed_at, approved_at...), en el día de Ecuador (lib/fechas). */
export function fechaEc(iso: string | null | undefined): string {
  return fechaEcComun(iso);
}

const desdeIso = (iso: string) => { const [a, m, d] = iso.split('-').map(Number); return new Date(a, m - 1, d); };

export function sumarDias(iso: string, dias: number): string {
  const d = desdeIso(iso);
  d.setDate(d.getDate() + dias);
  return isoLocal(d);
}

// Meses desde una fecha base, sin correr el día: el 31/01 + 1 mes = 28/02 (o 29) y + 2 meses = 31/03.
export function sumarMeses(iso: string, meses: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  const total = (m - 1) + meses;
  const anio = a + Math.floor(total / 12);
  const mes = ((total % 12) + 12) % 12;
  const ultimo = new Date(anio, mes + 1, 0).getDate();
  return isoLocal(new Date(anio, mes, Math.min(d, ultimo)));
}

const MESES_POR_FRECUENCIA: Record<Exclude<Frecuencia, 'unica' | 'semanal'>, number> = { mensual: 1, trimestral: 3, semestral: 6, anual: 12 };

// n-ésima fecha de la programación (n = 0 es la fecha de inicio). "única" solo tiene la de inicio.
export function fechaN(inicio: string, frecuencia: Frecuencia, n: number): string | null {
  if (n === 0) return inicio;
  if (frecuencia === 'unica') return null;
  if (frecuencia === 'semanal') return sumarDias(inicio, 7 * n);
  return sumarMeses(inicio, MESES_POR_FRECUENCIA[frecuencia] * n);
}

/**
 * Próxima fecha de una programación después de la última inspección generada (o la de inicio si no hay ninguna).
 * null si es "única" y ya se generó, o si pasa de la fecha de fin.
 */
export function proximaFecha(p: Pick<FilaProgramacion, 'frequency' | 'start_date' | 'end_date'>, ultima: string | null): string | null {
  let siguiente: string | null = null;
  if (!ultima) siguiente = p.start_date;
  else {
    for (let n = 0; n < 5000; n++) {
      const f = fechaN(p.start_date, p.frequency, n);
      if (!f) return null;
      if (f > ultima) { siguiente = f; break; }
    }
  }
  if (!siguiente) return null;
  if (p.end_date && siguiente > p.end_date) return null;
  return siguiente;
}

// ── Hallazgos ────────────────────────────────────────────────────────────────

/** Estado que se muestra: "vencido" si la fecha límite ya pasó y no está cerrado (se calcula, no se guarda). */
export function estadoHallazgo(h: Pick<FilaHallazgo, 'status' | 'due_date'>, hoy: Date = new Date()): EstadoHallazgo {
  if (h.status === 'cerrado') return 'cerrado';
  if (h.due_date && h.due_date < isoLocal(hoy)) return 'vencido';
  return h.status;
}

// Plazos SUGERIDOS y editables por nivel de riesgo: los de los niveles de acción del INSST acordados el 27/10/2026
// (§17 ítem 164: alto = importante, 7 días; medio = moderado, 30 días; bajo = tolerable, 90 días). La orden de F3a pide
// 3 niveles (bajo, medio, alto); el "crítico" de 24 h del ítem 164 no está en F3a. POR VALIDAR con Kevin.
export const PLAZO_SUGERIDO_DIAS: Record<Riesgo, number> = { alto: 7, medio: 30, bajo: 90 };
export const FUENTE_PLAZOS = 'Sugerido por StorSafe (niveles de acción del INSST): editable, por validar';
export const plazoSugerido = (riesgo: Riesgo, desde: string) => sumarDias(desde, PLAZO_SUGERIDO_DIAS[riesgo]);

export function resumenHallazgos(hs: Pick<FilaHallazgo, 'status' | 'due_date'>[], hoy: Date = new Date()): { abiertos: number; vencidos: number } {
  let abiertos = 0, vencidos = 0;
  for (const h of hs) {
    const e = estadoHallazgo(h, hoy);
    if (e !== 'cerrado') abiertos++;
    if (e === 'vencido') vencidos++;
  }
  return { abiertos, vencidos };
}

// ── Ítems y respuestas ───────────────────────────────────────────────────────

export const itemsDe = (f: Pick<FotoPlantilla, 'sections'> | null | undefined): ItemFoto[] => (f?.sections ?? []).flatMap(s => s.items);

/** ¿La respuesta es una falla? cumple → "no cumple"; si_no → la respuesta marcada como falla; número → fuera de su rango. */
export function esFalla(item: Pick<ItemFoto, 'response_type' | 'fail_value' | 'num_min' | 'num_max'>, r: Pick<FilaRespuesta, 'value' | 'number_value'> | undefined): boolean {
  if (!r) return false;
  if (item.response_type === 'cumple') return r.value === 'no_cumple';
  if (item.response_type === 'si_no') return !!item.fail_value && r.value === item.fail_value;
  if (item.response_type === 'numero') {
    if (r.number_value === null || r.number_value === undefined) return false;
    return (item.num_min !== null && item.num_min !== undefined && r.number_value < item.num_min)
      || (item.num_max !== null && item.num_max !== undefined && r.number_value > item.num_max);
  }
  return false;
}

export function respondido(item: Pick<ItemFoto, 'response_type'>, r: Pick<FilaRespuesta, 'value' | 'number_value' | 'text_value'> | undefined, fotos: number): boolean {
  switch (item.response_type as TipoRespuesta) {
    case 'cumple': case 'si_no': return !!r?.value;
    case 'numero': return r?.number_value !== null && r?.number_value !== undefined;
    case 'texto': return !!r?.text_value?.trim();
    case 'foto': return fotos > 0;
  }
  return false;
}

/** Foto obligatoria: siempre, o en "no cumple" (por defecto, K5). Con "No aplica" nunca se exige. */
export function fotoObligatoria(item: Pick<ItemFoto, 'photo_rule' | 'response_type' | 'fail_value' | 'num_min' | 'num_max' | 'required'>,
  r: Pick<FilaRespuesta, 'value' | 'number_value'> | undefined): boolean {
  if (r?.value === 'no_aplica') return false;
  if (item.response_type === 'foto') return item.required;
  if (item.photo_rule === 'obligatoria') return true;
  if (item.photo_rule === 'obligatoria_no_cumple') return esFalla(item, r);
  return false;
}

export const porItem = <T extends { item_key: string | null }>(lista: T[]) => {
  const m = new Map<string, T[]>();
  for (const x of lista) if (x.item_key) m.set(x.item_key, [...(m.get(x.item_key) ?? []), x]);
  return m;
};

export interface Faltante { itemId: string | null; texto: string }

/** Lo que falta para firmar: obligatorias sin responder, fotos obligatorias, fecha, firmantes y cédulas. */
export function faltantesParaFirmar(
  insp: Pick<FilaInspeccion, 'status' | 'performed_date' | 'template_snapshot'>,
  respuestas: FilaRespuesta[], fotos: Pick<FilaArchivo, 'item_key'>[],
  firmantes: Pick<FirmaInspeccion, 'rol' | 'nombre' | 'cedula'>[], hoy: Date = new Date(),
): Faltante[] {
  const f: Faltante[] = [];
  if (insp.status !== 'en_curso') f.push({ itemId: null, texto: 'Solo se firma una inspección en curso.' });
  if (!insp.performed_date) f.push({ itemId: null, texto: 'Falta la fecha en que se realizó la inspección.' });
  else if (insp.performed_date > isoLocal(hoy)) f.push({ itemId: null, texto: 'La fecha realizada no puede ser futura.' });
  const resp = new Map(respuestas.map(r => [r.item_key, r]));
  const nFotos = new Map<string, number>();
  for (const x of fotos) if (x.item_key) nFotos.set(x.item_key, (nFotos.get(x.item_key) ?? 0) + 1);
  itemsDe(insp.template_snapshot).forEach((it, i) => {
    const r = resp.get(it.id);
    const n = nFotos.get(it.id) ?? 0;
    if (it.required && !respondido(it, r, n)) f.push({ itemId: it.id, texto: `Responde el ítem ${i + 1}: ${it.text}` });
    else if (it.response_type !== 'foto' && fotoObligatoria(it, r) && n === 0) f.push({ itemId: it.id, texto: `Falta la foto del ítem ${i + 1}: ${it.text}` });
  });
  const inspector = firmantes.find(x => x.rol === 'inspector');
  if (!inspector || !inspector.nombre.trim() || !inspector.cedula.trim()) f.push({ itemId: null, texto: 'Falta la firma del inspector (nombre y cédula).' });
  for (const x of firmantes) {
    if (x.cedula.trim() && !/^\d{10}$/.test(x.cedula.trim())) f.push({ itemId: null, texto: `La cédula de ${ETIQUETA_ROL_FIRMA[x.rol]} debe tener 10 dígitos.` });
  }
  return f;
}

export interface PropuestaHallazgo {
  item_key: string;
  description: string;
  risk_level: Riesgo;
  due_date: string;
  corrective_action: string | null;
}

/**
 * Hallazgos automáticos: un ítem con "no cumple genera hallazgo" y una respuesta que es falla, que todavía no tiene su
 * hallazgo automático (único por inspección e ítem en la base). La descripción es el ítem y su observación.
 */
export function hallazgosAutomaticos(foto: FotoPlantilla, respuestas: FilaRespuesta[], existentes: Pick<FilaHallazgo, 'item_key' | 'origin'>[], fecha: string): PropuestaHallazgo[] {
  const ya = new Set(existentes.filter(h => h.origin === 'automatico' && h.item_key).map(h => h.item_key as string));
  const resp = new Map(respuestas.map(r => [r.item_key, r]));
  return itemsDe(foto).flatMap(it => {
    const r = resp.get(it.id);
    if (!it.creates_finding || ya.has(it.id) || !esFalla(it, r)) return [];
    const nota = r?.note?.trim();
    return [{ item_key: it.id, description: `${it.text}${nota ? ` — ${nota}` : ''}`.slice(0, 2000), risk_level: it.default_risk,
      due_date: plazoSugerido(it.default_risk, fecha), corrective_action: null }];
  });
}

// ── Reglas que ya existen en el código (no se duplican en la plantilla) ──────

/** Texto de la regla de un ítem según la sede. 'pe:extintor_distancia' = la distancia del plan de emergencias. */
export function textoRegla(ruleKey: string | null | undefined, cantonSede: string | null | undefined): string | null {
  if (ruleKey === 'pe:extintor_distancia') {
    const r = cantonSede === '1701' ? EXTINTOR_DISTANCIA_QUITO : EXTINTOR_DISTANCIA_NACIONAL;
    return `Distancia máxima de recorrido hasta un extintor: ${r.valor} m (${r.norma}, ${r.articulo_o_tabla}${cantonSede === '1701' ? ', Quito' : ''}).`;
  }
  return null;
}

// ── Vista tipo tabla ─────────────────────────────────────────────────────────

export type EstadoVisible = EstadoInspeccion | 'atrasada';

/** Programada con la fecha ya pasada = atrasada (se calcula). */
export function estadoVisible(i: Pick<FilaInspeccion, 'status' | 'scheduled_date'>, hoy: Date = new Date()): EstadoVisible {
  if (i.status === 'programada' && i.scheduled_date && i.scheduled_date < isoLocal(hoy)) return 'atrasada';
  return i.status;
}

export const fechaInspeccion = (i: Pick<FilaInspeccion, 'performed_date' | 'scheduled_date'>) => i.performed_date ?? i.scheduled_date ?? '';

export interface FilaTabla {
  id: string;
  estado: EstadoVisible;
  plantilla: string;
  sedeId: string;
  sede: string;
  area: string;
  responsable: string;
  /** Activo vinculado (código · nombre) o "—" (Mantenimiento M1). */
  activo: string;
  fecha: string;
  realizada: boolean;
  abiertos: number;
  vencidos: number;
}

export function filasTabla(inspecciones: FilaInspeccion[], hallazgos: FilaHallazgo[], sedes: SedeMin[], trabajadores: TrabajadorMin[], hoy: Date = new Date(),
  activos: ActivoMin[] = []): FilaTabla[] {
  const sede = new Map(sedes.map(s => [s.id, s.name]));
  const trab = new Map(trabajadores.map(t => [t.id, t.full_name]));
  const act = new Map(activos.map(a => [a.id, `${a.code} · ${a.name}`]));
  return inspecciones.map(i => {
    const r = resumenHallazgos(hallazgos.filter(h => h.inspection_id === i.id), hoy);
    return {
      id: i.id, estado: estadoVisible(i, hoy), plantilla: i.template_name, sedeId: i.site_id, sede: sede.get(i.site_id) ?? '—',
      area: i.area ?? '', responsable: (i.responsible_worker_id && trab.get(i.responsible_worker_id)) || i.responsible_role || '—',
      activo: (i.asset_id && act.get(i.asset_id)) || '—',
      fecha: fechaInspeccion(i), realizada: !!i.performed_date, abiertos: r.abiertos, vencidos: r.vencidos,
    };
  }).sort((a, b) => b.fecha.localeCompare(a.fecha));
}

export interface FiltrosTabla { estado: EstadoVisible | ''; plantilla: string; sede: string; texto: string }
export const FILTROS_VACIOS: FiltrosTabla = { estado: '', plantilla: '', sede: '', texto: '' };

export function filtrarFilas(filas: FilaTabla[], f: FiltrosTabla): FilaTabla[] {
  const t = f.texto.trim().toLowerCase();
  return filas.filter(x => (!f.estado || x.estado === f.estado) && (!f.plantilla || x.plantilla === f.plantilla) && (!f.sede || x.sedeId === f.sede)
    && (!t || `${x.plantilla} ${x.area} ${x.responsable} ${x.sede} ${x.activo === '—' ? '' : x.activo}`.toLowerCase().includes(t)));
}

export type Agrupacion = 'ninguna' | 'estado' | 'sede';
const ORDEN_ESTADO: EstadoVisible[] = ['atrasada', 'en_curso', 'programada', 'completada', 'cancelada'];

export function agruparFilas(filas: FilaTabla[], por: Agrupacion): { clave: string; filas: FilaTabla[] }[] {
  if (por === 'ninguna') return [{ clave: '', filas }];
  const grupos = new Map<string, FilaTabla[]>();
  for (const x of filas) {
    const k = por === 'estado' ? x.estado : x.sede;
    grupos.set(k, [...(grupos.get(k) ?? []), x]);
  }
  const claves = Array.from(grupos.keys());
  claves.sort(por === 'estado'
    ? (a, b) => ORDEN_ESTADO.indexOf(a as EstadoVisible) - ORDEN_ESTADO.indexOf(b as EstadoVisible)
    : (a, b) => a.localeCompare(b));
  return claves.map(clave => ({ clave, filas: grupos.get(clave) as FilaTabla[] }));
}

// ── Calendario ───────────────────────────────────────────────────────────────

/** Semanas del mes (lunes a domingo); null = día de otro mes. */
export function semanasDelMes(anio: number, mes: number): (string | null)[][] {
  const primero = new Date(anio, mes, 1);
  const dias = new Date(anio, mes + 1, 0).getDate();
  const desfase = (primero.getDay() + 6) % 7; // lunes = 0
  const celdas: (string | null)[] = Array(desfase).fill(null);
  for (let d = 1; d <= dias; d++) celdas.push(isoLocal(new Date(anio, mes, d)));
  while (celdas.length % 7) celdas.push(null);
  const semanas: (string | null)[][] = [];
  for (let i = 0; i < celdas.length; i += 7) semanas.push(celdas.slice(i, i + 7));
  return semanas;
}

export function porFecha<T extends { fecha: string }>(filas: T[]): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const f of filas) if (f.fecha) m.set(f.fecha, [...(m.get(f.fecha) ?? []), f]);
  return m;
}

// ── Plantillas propias ───────────────────────────────────────────────────────

export interface ItemEditable {
  id?: string;
  text: string;
  help?: string | null;
  response_type: TipoRespuesta;
  unit?: string | null;
  num_min?: number | null;
  num_max?: number | null;
  required: boolean;
  creates_finding: boolean;
  fail_value?: 'si' | 'no' | null;
  default_risk: Riesgo;
  photo_rule: ItemFoto['photo_rule'];
  anexo_item?: string | null;
  reference?: string | null;
  rule_key?: string | null;
}
export interface SeccionEditable { id?: string; title: string; items: ItemEditable[] }

export const itemVacio = (): ItemEditable => ({
  text: '', response_type: 'cumple', required: true, creates_finding: true, default_risk: 'medio', photo_rule: 'obligatoria_no_cumple',
});

export const PUEDE_FALLAR: TipoRespuesta[] = ['cumple', 'si_no', 'numero'];

/** Errores de una plantilla propia (en español, para mostrarlos todos juntos). */
export function validarPlantilla(nombre: string, secciones: SeccionEditable[]): string[] {
  const e: string[] = [];
  if (!nombre.trim()) e.push('Escribe el nombre de la plantilla.');
  if (secciones.length === 0) e.push('Agrega al menos una sección.');
  secciones.forEach((s, i) => {
    if (!s.title.trim()) e.push(`La sección ${i + 1} no tiene título.`);
    if (s.items.length === 0) e.push(`La sección ${i + 1} no tiene ítems.`);
    s.items.forEach((it, j) => {
      const donde = `Sección ${i + 1}, ítem ${j + 1}`;
      if (!it.text.trim()) e.push(`${donde}: escribe la pregunta.`);
      if (it.creates_finding && !PUEDE_FALLAR.includes(it.response_type)) e.push(`${donde}: un ítem de texto o foto no puede generar hallazgo.`);
      if (it.response_type === 'si_no' && it.creates_finding && !it.fail_value) e.push(`${donde}: elige qué respuesta (Sí o No) genera el hallazgo.`);
      if (it.response_type === 'numero' && it.num_min != null && it.num_max != null && it.num_min > it.num_max) e.push(`${donde}: el mínimo es mayor que el máximo.`);
    });
  });
  return e;
}

/** Estructura para insp_guardar_estructura: solo los campos que corresponden al tipo. */
export function estructuraParaGuardar(secciones: SeccionEditable[]) {
  return secciones.map(s => ({
    ...(s.id ? { id: s.id } : {}), title: s.title.trim(),
    items: s.items.map(it => ({
      ...(it.id ? { id: it.id } : {}), text: it.text.trim(), help: it.help?.trim() || null, response_type: it.response_type,
      unit: it.response_type === 'numero' ? it.unit?.trim() || null : null,
      num_min: it.response_type === 'numero' ? it.num_min ?? null : null, num_max: it.response_type === 'numero' ? it.num_max ?? null : null,
      required: it.required, creates_finding: PUEDE_FALLAR.includes(it.response_type) && it.creates_finding,
      fail_value: it.response_type === 'si_no' ? it.fail_value ?? null : null, default_risk: it.default_risk,
      photo_rule: it.photo_rule, anexo_item: it.anexo_item?.trim() || null, reference: it.reference?.trim() || null, rule_key: it.rule_key || null,
    })),
  }));
}

export function editableDesdeFoto(f: FotoPlantilla): SeccionEditable[] {
  return f.sections.map(s => ({
    id: s.id, title: s.title,
    items: s.items.map(it => ({
      id: it.id, text: it.text, help: it.help, response_type: it.response_type, unit: it.unit, num_min: it.num_min, num_max: it.num_max,
      required: it.required, creates_finding: it.creates_finding, fail_value: it.fail_value, default_risk: it.default_risk,
      photo_rule: it.photo_rule, anexo_item: it.anexo_item, reference: it.reference, rule_key: it.rule_key,
    })),
  }));
}

// ── Programaciones ───────────────────────────────────────────────────────────

export function validarProgramacion(p: Pick<FilaProgramacion, 'template_id' | 'frequency' | 'start_date' | 'end_date'>): string[] {
  const e: string[] = [];
  if (!p.template_id) e.push('Elige la plantilla.');
  if (!p.start_date) e.push('Elige la fecha de la primera inspección.');
  if (p.end_date && p.start_date && p.end_date < p.start_date) e.push('La fecha de fin es anterior a la de inicio.');
  if (p.frequency === 'unica' && p.end_date) e.push('Una inspección única no lleva fecha de fin.');
  return e;
}

// ── Firma (K6) ───────────────────────────────────────────────────────────────

/** Texto canónico que se firma (SHA-256): datos de la inspección, respuestas en orden y el firmante. */
export function contenidoParaFirma(i: Pick<FilaInspeccion, 'id' | 'company_id' | 'site_id' | 'performed_date' | 'template_snapshot' | 'observations'>,
  respuestas: FilaRespuesta[], f: Pick<FirmaInspeccion, 'rol' | 'nombre' | 'cedula' | 'firmado_en'>): string {
  const resp = new Map(respuestas.map(r => [r.item_key, r]));
  return JSON.stringify({
    inspeccion: i.id, empresa: i.company_id, sede: i.site_id, fecha: i.performed_date, plantilla: i.template_snapshot.template_id,
    version: i.template_snapshot.version, observaciones: i.observations ?? null,
    respuestas: itemsDe(i.template_snapshot).map(it => {
      const r = resp.get(it.id);
      return [it.id, r?.value ?? null, r?.number_value ?? null, r?.text_value ?? null, r?.note ?? null];
    }),
    firmante: { rol: f.rol, nombre: f.nombre.trim(), cedula: f.cedula.trim(), en: f.firmado_en },
  });
}

export const nombreArchivoPdf = (i: Pick<FilaInspeccion, 'template_name' | 'performed_date' | 'scheduled_date'>) =>
  `Inspeccion_${i.template_name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 50)}_${fechaInspeccion(i)}.pdf`;
